import OfflineBanner from "@funded-here-interface/common/src/components/OfflineBanner";
import { setToken } from "@funded-here-interface/common/src/features/auth/authSlice_Admin";
import { useVersionCheck } from "@funded-here-interface/common/src/hooks/useVersionCheck";
import { JwtProvider } from "@funded-here-interface/common/src/providers/JwtProvider";
import { SessionInterceptorProvider } from "@funded-here-interface/common/src/providers/SessionInterceptorProvider";
import { Center, Loader } from "@mantine/core";
import { lazy, Suspense } from "react";
import { QueryClientProvider } from "react-query";

import "./App.css";
import { Provider, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { PersistGate } from "redux-persist/integration/react";

import OperationAndRiskLayout from "./layouts/OperationAndRisk/OperationAndRiskLayout";
import ProductLayout from "./layouts/ProductLayout/ProductLayout";
import SalesSupportLayout from "./layouts/SalesSupportLayout/SalesSupportLayout";
import { client } from "./reactQuery";

import "react-toastify/dist/ReactToastify.css";
import { NOTES_PATH, PM_DASHBOARD_REDIRECTS } from "./screens/PMDashboard/notesRoutes";
import { store, persistor, RootState } from "./store";
import { adminResetActions } from "./Utils/adminResetActions";

const AcknowledgementLayout = lazy(
  () =>
    import("@funded-here-interface/common/src/Layouts/AcknowledgementLayout/AcknowledgementLayout"),
);
const LoginLayout = lazy(
  () => import("@funded-here-interface/common/src/Layouts/LoginLayout/LoginLayout"),
);
const PasswordReset = lazy(
  () => import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordReset"),
);
const PasswordResetMsg = lazy(
  () => import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordResetMsg"),
);
const Navbar = lazy(
  () => import("@funded-here-interface/common/src/components/MobileNavbar/Navbar"),
);
const TwoFAQRPageCommon = lazy(
  () => import("@funded-here-interface/common/src/pages/TwoFA/TwoFAQRCode"),
);
const NoteCreationPage = lazy(() => import("./screens/StructuredNote/NoteCreation"));
const OrgDetailsPage = lazy(() => import("./screens/OrgDetails/OrgDetailsPage"));
const Issusingspv = lazy(() => import("./screens/IssusingSPV.tsx/Issusingspv"));
const UserInvitation = lazy(() => import("./screens/UserInvitation/UserInvitation"));
const Dashboard = lazy(() => import("./screens/PMDashboard/DashBoard"));
// Lazy although it only redirects: it validates ?status= with schemas/sku, which pulls in zod.
const NotesRedirect = lazy(() => import("./screens/PMDashboard/NotesRedirect"));
const CampaignList = lazy(() => import("./screens/CampaignList/CampaignList"));
const CreateCampaign = lazy(() => import("./screens/CreateCampaign/CreateCampaign"));
const Login = lazy(() => import("./screens/Login/Login"));
const Notelist = lazy(() => import("./screens/Notelist/Notelist"));
const PmVerifySkuLoans = lazy(() => import("./screens/PmVerifySkuLoans/PmVerifySkuLoans"));
const IndividualList = lazy(() => import("./screens/IndividualList/IndividualList"));
const InvestorSummary = lazy(
  () => import("./screens/PMDashboard/Components/Investor/InvestorSummary"),
);
const Merchant = lazy(() => import("./screens/PMDashboard/Components/MerchantSummary/Merchant"));
const Skusummary = lazy(() => import("./screens/PMDashboard/Components/SKUSummary/Skusummary"));
const VirtualAccounts = lazy(() => import("./screens/VirtualAccounts/VirtualAccounts"));
const MerchantExternalVirtualAccounts = lazy(
  () => import("./screens/MerchantExternalVirtualAccounts/MerchantExternalVirtualAccounts"),
);
const LoanDetails = lazy(() => import("./screens/LoanDetails/LoanDetails"));
const PmVerifySkuLoanDetails = lazy(
  () => import("./screens/PmVerifySkuLoanDetails/PmVerifySkuLoanDetails"),
);
const Repayment = lazy(() => import("./screens/PMRepayment/Repayment"));
const IssueNote = lazy(() => import("./screens/IssueSNOverview/IssueNote"));
const CollaborationProposal = lazy(
  () => import("./screens/CollaborationProposal/CollaborationProposal"),
);
const Withdraw = lazy(() => import("./screens/Withdraw/Withdraw"));
const FeatureFlag = lazy(() => import("./screens/FeatureFlag/FeatureFlag"));
const ExternalAccounts = lazy(() => import("./screens/ExternalAccounts/ExternalAccounts"));
const Audit = lazy(() => import("./screens/Audit/Audit"));
const Inventory = lazy(() => import("./screens/InventoryOrders/Inventory/Inventory"));
const Orders = lazy(() => import("./screens/InventoryOrders/Orders/Orders"));
const FundPull = lazy(() => import("./screens/InventoryOrders/FundPull/FundPull"));
const Onboarding = lazy(() => import("./screens/SalesOnboarding/Onboarding"));
const OnboardingFormWrapper = lazy(() => import("./screens/SalesOnboarding/OnboardingFormWrapper"));
const FundPullAutoRefund = lazy(() => import("./screens/MerchantConfiguration/FundPullAutoRefund"));
const Scheme = lazy(() => import("./screens/Scheme/Scheme"));
const RepaymentScheme = lazy(() => import("./screens/RepaymentScheme/RepaymentScheme"));
const RequestApproval = lazy(() => import("./screens/RequestApproval/RequestApproval"));
const CreateRequestApproval = lazy(
  () => import("./screens/CreateRequestApproval/CreateRequestApproval"),
);
const MerchantConfigurator = lazy(
  () => import("./screens/MerchantConfigurator/MerchantConfiguratorPage"),
);
const ThirdPartyLogisticsCostReimbursement = lazy(
  () =>
    import("./screens/ThirdPartyLogisticsCostReimbursement/ThirdPartyLogisticsCostReimbursement"),
);
const FundingLimit = lazy(() => import("./screens/MerchantFundingLimit/MerchantFundingLimit"));

type LayoutType = "product" | "operationAndRisk" | "salesSupport";

interface PrivateRouteConfig {
  path: string;
  component: React.LazyExoticComponent<React.ComponentType>;
  layout: LayoutType;
}

const layoutMap: Record<LayoutType, React.ComponentType<{ content: React.ReactNode }>> = {
  product: ProductLayout,
  operationAndRisk: OperationAndRiskLayout,
  salesSupport: SalesSupportLayout,
};

export const privateRoutes: PrivateRouteConfig[] = [
  // Product layout routes
  { path: NOTES_PATH, component: Dashboard, layout: "product" },
  { path: "/withdrawal", component: Withdraw, layout: "product" },
  { path: "/investorsummary", component: InvestorSummary, layout: "product" },
  { path: "/merchant", component: Merchant, layout: "product" },
  { path: "/snusummary", component: Skusummary, layout: "product" },
  { path: "/repayment/:id", component: Repayment, layout: "product" },
  { path: "/note-list", component: Notelist, layout: "product" },
  { path: "/create-campaign", component: CreateCampaign, layout: "product" },
  { path: "/campaign-list", component: CampaignList, layout: "product" },
  { path: "/funding-details", component: LoanDetails, layout: "product" },
  { path: "/verify-sku-loans", component: PmVerifySkuLoans, layout: "product" },
  {
    path: "/verify-sku-loan-details",
    component: PmVerifySkuLoanDetails,
    layout: "product",
  },
  { path: "/issue-note/:id", component: IssueNote, layout: "product" },
  { path: "/issuer", component: Issusingspv, layout: "product" },
  { path: "/user-invitation", component: UserInvitation, layout: "product" },
  {
    path: "/structured-note/note-creation",
    component: NoteCreationPage,
    layout: "product",
  },
  { path: "/virtual-accounts", component: VirtualAccounts, layout: "product" },
  {
    path: "/external-accounts",
    component: ExternalAccounts,
    layout: "product",
  },
  { path: "/audit", component: Audit, layout: "product" },
  { path: "/feature-flags", component: FeatureFlag, layout: "product" },
  { path: "/inventory", component: Inventory, layout: "product" },
  { path: "/orders", component: Orders, layout: "product" },
  { path: "/fund-pull", component: FundPull, layout: "product" },
  {
    path: "/fund-pull-auto-refund",
    component: FundPullAutoRefund,
    layout: "product",
  },
  { path: "/schemes", component: Scheme, layout: "product" },
  {
    path: "/repayment-scheme",
    component: RepaymentScheme,
    layout: "product",
  },
  { path: "/request-approval", component: RequestApproval, layout: "product" },
  { path: "/create-request-approval", component: CreateRequestApproval, layout: "product" },
  {
    path: "/third-party-logistics-cost-reimbursement",
    component: ThirdPartyLogisticsCostReimbursement,
    layout: "product",
  },
  {
    path: "/funding-limit-details-pm",
    component: FundingLimit,
    layout: "product",
  },
  // Operation & Risk layout routes
  { path: "/org-list", component: OrgDetailsPage, layout: "operationAndRisk" },
  {
    path: "/individual-list",
    component: IndividualList,
    layout: "operationAndRisk",
  },
  {
    path: "/merchant-configurator",
    component: MerchantConfigurator,
    layout: "operationAndRisk",
  },

  // Sales Support layout routes
  {
    path: "/sales-support/:tabValue",
    component: CollaborationProposal,
    layout: "salesSupport",
  },
  { path: "/onboarding", component: Onboarding, layout: "salesSupport" },
  {
    path: "/onboardingForm",
    component: OnboardingFormWrapper,
    layout: "salesSupport",
  },
  {
    path: "/merchant-collection-accounts",
    component: MerchantExternalVirtualAccounts,
    layout: "salesSupport",
  },
  {
    path: "/sales-support/funding-limit-details",
    component: FundingLimit,
    layout: "salesSupport",
  },
];

const SuspenseFallback = (
  <Center style={{ height: "100vh" }}>
    <Loader size="lg" />
  </Center>
);

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const token = useSelector((state: RootState) => state.auth.token);

  return !token || token === "none" ? <Navigate to="/login" replace /> : <>{children}</>;
};

function App() {
  useVersionCheck();

  return (
    <QueryClientProvider client={client}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ToastContainer />
          <OfflineBanner />
          <BrowserRouter>
            <SessionInterceptorProvider
              persistor={persistor}
              resetActions={adminResetActions}
              tokenSelector={(state: RootState) => state.auth}
            >
              <JwtProvider
                tokenSelector={(state: RootState) => state.auth}
                setTokenAction={setToken}
                persistor={persistor}
                resetActions={adminResetActions}
              >
                <Suspense fallback={SuspenseFallback}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<LoginLayout content={<Login />} />} />
                    <Route
                      path="/two-auth-QR-code"
                      element={<Navbar content={<TwoFAQRPageCommon />} />}
                    />
                    <Route
                      path="/password-reset"
                      element={<AcknowledgementLayout content={<PasswordReset />} />}
                    />
                    <Route
                      path="/reset-message"
                      element={<AcknowledgementLayout content={<PasswordResetMsg />} />}
                    />

                    {/* Private routes */}
                    {privateRoutes.map(({ path, component: Screen, layout }) => {
                      const Layout = layoutMap[layout];
                      return (
                        <Route
                          key={path}
                          path={path}
                          element={
                            <PrivateRoute>
                              <Layout content={<Screen />} />
                            </PrivateRoute>
                          }
                        />
                      );
                    })}

                    {/* Old PM dashboard URLs. Outside PrivateRoute: they land on NOTES_PATH,
                        which is private. */}
                    {PM_DASHBOARD_REDIRECTS.map(({ path, status }) => (
                      <Route key={path} path={path} element={<NotesRedirect status={status} />} />
                    ))}

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
                </Suspense>
              </JwtProvider>
            </SessionInterceptorProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
